import { createContext, useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Sun, Moon, CalendarDays, UserCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePreview } from "@/context/PreviewContext";
import { PreviewBanner } from "@/components/PreviewBanner";

/* ---------------- Producer theme (light default + dark toggle) --------------- */
const THEME_KEY = "goose-producer-theme";
const ProducerThemeContext = createContext(null);

export const ProducerThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light"; } catch { return "light"; }
  });
  const setTheme = (t) => {
    setThemeState(t);
    try { localStorage.setItem(THEME_KEY, t); } catch { /* ignore */ }
  };
  const toggle = () => setTheme(theme === "dark" ? "light" : "dark");
  return (
    <ProducerThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ProducerThemeContext.Provider>
  );
};

export const useProducerTheme = () =>
  useContext(ProducerThemeContext) || { theme: "light", toggle: () => {}, setTheme: () => {} };

/* Theme-aware surface classes for portalled content (dialogs, selects) that
   render outside the .goose-producer wrapper. */
export const portalSurface = (theme) =>
  theme === "dark"
    ? "border-[#2A313C] bg-[#171C23] text-[#F1F4F8]"
    : "border-[#E4E7EC] bg-white text-[#171C23]";

export const chartTheme = (theme) => ({
  tooltip: theme === "dark"
    ? { background: "#1E242D", border: "1px solid #2A313C", borderRadius: 12, color: "#F1F4F8" }
    : { background: "#FFFFFF", border: "1px solid #E4E7EC", borderRadius: 12, color: "#171C23" },
  axis: theme === "dark" ? "#8A94A3" : "#8A94A3",
});

/* ---------------- Shared producer primitives --------------- */
export const ProducerShell = ({ children }) => {
  const { theme } = useProducerTheme();
  return (
    <div className={`goose-producer p-workspace ${theme === "dark" ? "theme-dark" : ""}`}>
      {children}
    </div>
  );
};

export const PLogo = ({ className = "", onClick }) => (
  <button
    onClick={onClick}
    data-testid="goose-logo"
    className={`font-display font-extrabold tracking-tight text-white ${className}`}
    style={{ letterSpacing: "0.02em" }}
  >
    GOOSE<span style={{ color: "var(--p-accent)" }}>.</span>
  </button>
);

export const PSectionLabel = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.2em] p-subtle">{children}</p>
);

export const PSpinner = () => (
  <div className="flex items-center justify-center py-16">
    <div className="h-8 w-8 animate-spin rounded-full border-2 p-border-c" style={{ borderTopColor: "var(--p-accent)" }} />
  </div>
);

export const PFadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------------- Producer top navigation --------------- */
export const ProducerNav = ({ active = "events" }) => {
  const { user, logout } = useAuth();
  const { preview } = usePreview();
  const { theme, toggle } = useProducerTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isAccount = active === "account" || location.pathname.startsWith("/producer/account");
  const displayName = preview ? preview.name : (user?.name || user?.email || "Producer");

  return (
    <>
      <PreviewBanner />
      <header className={`p-header z-30 ${preview ? "" : "sticky top-0"}`}>
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4 sm:gap-6 sm:px-6">
          <PLogo className="text-xl" onClick={() => navigate("/producer")} />

          <nav className="flex items-center gap-4 text-sm sm:gap-6">
            <button
              data-testid="nav-events"
              onClick={() => navigate("/producer")}
              className={`p-navlink ${!isAccount ? "active" : ""}`}
            >
              Events
            </button>
            <button
              data-testid="nav-account"
              onClick={() => navigate("/producer/account")}
              className={`p-navlink ${isAccount ? "active" : ""}`}
            >
              Account
            </button>
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <button
              data-testid="theme-toggle-btn"
              onClick={toggle}
              aria-label="Toggle colour theme"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors"
              style={{ borderColor: "rgba(255,255,255,0.14)", color: "var(--p-header-muted)" }}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold text-white">{displayName}</p>
              <p className="text-xs" style={{ color: "var(--p-header-muted)" }}>
                {preview ? "Preview (Admin)" : (user?.producer?.business_name || "Producer")}
              </p>
            </div>
            {user?.picture && !preview ? (
              <img src={user.picture} alt="" className="hidden h-9 w-9 rounded-full sm:block" style={{ border: "1px solid rgba(255,255,255,0.14)" }} />
            ) : (
              <div className="hidden h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white sm:flex" style={{ background: "var(--p-accent)" }}>
                {displayName[0]}
              </div>
            )}
            {!preview && (
              <button
                data-testid="logout-btn"
                onClick={async () => { await logout(); navigate("/"); }}
                className="flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-2 text-xs font-semibold transition-colors sm:px-3.5"
                style={{ border: "1px solid rgba(255,255,255,0.14)", color: "var(--p-header-muted)" }}
              >
                <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sign out</span>
              </button>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export const ACCOUNT_ICON = UserCircle;
export const EVENTS_ICON = CalendarDays;
